import React from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Cross-link to our sister site: Pula (Fair Connect).
 * Emphasises kinship, not commerce. Opens in new tab.
 */
export default function SisterSite() {
  return (
    <section className="py-20 md:py-28 border-t mv-hairline" data-testid="sister-site-section"
             style={{ background: "linear-gradient(180deg, var(--mv-bg) 0%, #fff 100%)" }}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Our Sister Community</span></div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display text-3xl md:text-5xl mv-green-text leading-[1.05]">
              Meet <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Pula</span> — rain of blessings.
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed max-w-2xl" style={{ color: "var(--mv-ink)" }}>
              Mavula is the home-ownership door. <strong>Pula</strong> is the whole community around it — a fair, honest place to <strong>connect, trade, learn and stay close to family</strong>. Free jobs board, 0% commission marketplace, free learning, private community, and a low-data mode that works even on 2G. Part of the same family. Same values. Same promise: dignity first.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {["Love", "Market", "Jobs", "Learn", "Wallet", "Students", "Lens", "Live"].map((p) => (
                <span key={p} className="text-[12.5px] px-3 py-1 rounded-full font-medium"
                      style={{ background: "rgba(10,59,40,0.08)", color: "var(--mv-green)" }}>
                  Pula {p}
                </span>
              ))}
            </div>

            <div className="mt-8">
              <motion.a
                whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
                href="https://fair-connect-2.emergent.host"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sister-site-visit"
                className="btn-gold inline-flex items-center gap-2"
              >
                Visit Pula <ExternalLink size={14}/>
              </motion.a>
            </div>

            <div className="mt-4 text-[12px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
              Pula is operated by our sister brand within the MAVULA Group. Opens in a new tab.
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              whileHover={{ y: -4 }}
              transition={{ duration: 0.35 }}
              className="rounded-3xl p-6 md:p-8 mv-green-bg text-white relative overflow-hidden"
            >
              <div className="absolute inset-0 opacity-15" style={{
                background: "radial-gradient(400px 200px at 80% 0%, var(--mv-gold), transparent 60%)"
              }}/>
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4"
                     style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(212,175,55,0.55)" }}>
                  <Sparkles size={13} style={{ color: "var(--mv-gold)" }} />
                  <span className="text-[11px] uppercase tracking-[0.24em] font-semibold">Rain of blessings</span>
                </div>
                <blockquote className="font-accent italic text-xl md:text-2xl leading-snug">
                  "One trusted place to connect, trade, work, learn, own your home, and stay close to family — for everyone, everywhere."
                </blockquote>
                <div className="mt-6 grid grid-cols-2 gap-3 text-[12.5px]">
                  {[
                    { k: "0%", d: "commission on homes" },
                    { k: "Free", d: "for job seekers" },
                    { k: "2G", d: "works on slow networks" },
                    { k: "Never", d: "sell your data" },
                  ].map((c) => (
                    <div key={c.k} className="p-3 rounded-xl"
                         style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.10)" }}>
                      <div className="font-display text-2xl" style={{ color: "var(--mv-gold)" }}>{c.k}</div>
                      <div className="text-white/85">{c.d}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
